import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import { trackEvent } from '../analytics';
import './LongTermTimelapseGuidePage.css';

// "How long is a time lapse" query cluster. Search Console shows the whole
// family — 30 second / 1 minute / 5 minute / 10 minute / 8 hour, plus "in real
// time" — impressing at positions 46-85 with no page of ours actually answering
// them. The calculator answers the planning version of the question; this page
// answers the lookup version, and hands the reader to the calculator once their
// shoot has a daily window or a storage ceiling.
//
// EVERY number on this page is stated at 30 fps playback and is pre-computed:
// frames = recording seconds ÷ interval, clip = frames ÷ 30. The charts assume
// the camera runs continuously across the stated recording time, which is why
// the long-term chart hands off to the calculator for real shooting windows.

const PLAYBACK_FPS = 30;

const TOC = [
  { id: 'howlong_formula', label: 'The formula' },
  { id: 'howlong_short', label: 'Chart: minutes-to-hours shoots' },
  { id: 'howlong_long', label: 'Chart: days-to-a-year shoots' },
  { id: 'howlong_reverse', label: 'I want a 30-second clip — how long do I record?' },
  { id: 'howlong_phone', label: 'Why your phone ignores all of this' },
  { id: 'howlong_faq', label: 'FAQ' },
];

// Derived, never hand-listed: a section added to TOC must also be reported to
// analytics, and a duplicated list would drift silently.
const SECTION_IDS = TOC.map((item) => item.id);

// Rows = recording time, columns = capture interval, cells = finished clip
// length at 30 fps.
const SHORT_CHART = {
  intervals: ['1 s', '2 s', '5 s', '10 s'],
  rows: [
    { recording: '10 min', clips: ['20 s', '10 s', '4 s', '2 s'] },
    { recording: '30 min', clips: ['1 m 0 s', '30 s', '12 s', '6 s'] },
    { recording: '1 hour', clips: ['2 m 0 s', '1 m 0 s', '24 s', '12 s'] },
    { recording: '3 hours', clips: ['6 m 0 s', '3 m 0 s', '1 m 12 s', '36 s'] },
    { recording: '8 hours', clips: ['16 m 0 s', '8 m 0 s', '3 m 12 s', '1 m 36 s'] },
  ],
};

const LONG_CHART = {
  intervals: ['1 min', '5 min', '15 min', '30 min'],
  rows: [
    { recording: '1 day', clips: ['48 s', '10 s', '3 s', '2 s'] },
    { recording: '1 week', clips: ['5 m 36 s', '1 m 7 s', '22 s', '11 s'] },
    { recording: '30 days', clips: ['24 m 0 s', '4 m 48 s', '1 m 36 s', '48 s'] },
    { recording: '90 days', clips: ['1 h 12 m', '14 m 24 s', '4 m 48 s', '2 m 24 s'] },
    { recording: '1 year', clips: ['4 h 52 m', '58 m 24 s', '19 m 28 s', '9 m 44 s'] },
  ],
};

// The same math read backwards: a 30-second clip at 30 fps is 900 frames, so
// each interval implies a recording time.
const REVERSE_CHART = [
  { interval: '1 s', record: '15 minutes' },
  { interval: '5 s', record: '1 h 15 m' },
  { interval: '30 s', record: '7.5 hours' },
  { interval: '1 min', record: '15 hours' },
  { interval: '5 min', record: '~3 days' },
];

const FAQ = [
  {
    q: 'How long is a 30 second time lapse in real time?',
    a: 'A 30-second clip at 30 fps playback is 900 frames. That is 15 minutes of real time at a 1-second interval, 75 minutes at a 5-second interval, and 7.5 hours at a 30-second interval.',
  },
  {
    q: 'How long is a 1 minute time lapse in real time?',
    a: 'A 1-minute clip at 30 fps playback is 1,800 frames: 30 minutes of real time at a 1-second interval, 2.5 hours at a 5-second interval, and 15 hours at a 30-second interval.',
  },
  {
    q: 'How long is a 5 minute time lapse?',
    a: 'A 5-minute clip at 30 fps playback is 9,000 frames: 2.5 hours of real time at a 1-second interval, and 12.5 hours at a 5-second interval.',
  },
  {
    q: 'How long is a 10 minute time lapse video?',
    a: 'A 10-minute clip at 30 fps playback is 18,000 frames: 5 hours of real time at a 1-second interval, and 25 hours at a 5-second interval.',
  },
  {
    q: 'How long would an 8 hour time lapse be?',
    a: 'Recording for 8 hours gives a 16-minute clip at a 1-second interval, 3 m 12 s at a 5-second interval, and 32 seconds at a 30-second interval — all at 30 fps playback.',
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/guides/how-long-is-a-time-lapse';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: 'How Long Is a Time Lapse? Length Chart + Real-Time Converter',
        description:
          'The formula behind time lapse length, conversion charts in both directions, and what each interval means for the finished clip.',
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Tweedia Studio', url: 'https://tweediastudio.com' },
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
        image: 'https://tweediastudio.com/tweedia_tour_poster.jpg',
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function HowLongIsATimelapsePage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: 'How Long Is a Time Lapse? Length Chart + Real-Time Converter | Tweedia Studio',
    description:
      'How long is a 30 second, 5 minute, or 8 hour time lapse? The exact formula, conversion charts in both directions, and a free calculator for long-term shoots.',
    canonicalPath: '/guides/how-long-is-a-time-lapse',
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const setSectionRef = useSectionViewTracking(SECTION_IDS);

  const handleStudioCta = () => {
    trackEvent('cta_click', { cta_id: 'howlong_try_studio', signed_in: signedIn });
    if (!signedIn) signIn();
  };

  const navClick = (target) => () => {
    trackEvent('nav_click', { target });
  };

  const studioCta = signedIn ? (
    <Link
      to="/studio"
      className="guide-primary-cta"
      data-testid="howlong-studio-cta"
      onClick={handleStudioCta}
    >
      Try Tweedia Studio
    </Link>
  ) : (
    <button
      type="button"
      className="guide-primary-cta"
      data-testid="howlong-studio-cta"
      onClick={handleStudioCta}
    >
      Try Tweedia Studio
    </button>
  );

  const renderChart = (chart, testId) => (
    <div className="guide-table-wrap">
      <table className="guide-table" data-testid={testId}>
        <thead>
          <tr>
            <th>Recording time</th>
            {chart.intervals.map((interval) => (
              <th key={interval}>{interval}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {chart.rows.map((row) => (
            <tr key={row.recording}>
              <td>{row.recording}</td>
              {row.clips.map((clip, i) => (
                <td key={chart.intervals[i]}>{clip}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="guide-page">
      <Header user={user} signIn={signIn} />
      <main className="guide-content" data-testid="howlong-content">
        <p className="guide-eyebrow">Guide</p>
        <h1>How Long Is a Time Lapse?</h1>
        <p className="guide-meta">
          The formula, both conversion charts, and the reverse lookup. 5 min read.
        </p>
        <p className="guide-lede">
          Final video length = recording time ÷ interval ÷ frame rate. Record for 8 hours at
          one photo every 5 seconds and you capture 5,760 frames — {PLAYBACK_FPS} frames a
          second on playback, so 3 minutes 12 seconds of video. Every number below is worked
          at {PLAYBACK_FPS} fps, the standard playback rate, and every chart runs in both
          directions: how long your clip will be, and how long you need to record to get the
          clip you want.
        </p>

        <nav className="guide-toc" aria-label="Table of contents">
          <p className="guide-toc-title">Contents</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section
          id="howlong_formula"
          data-section-id="howlong_formula"
          ref={setSectionRef('howlong_formula')}
        >
          <h2>The formula</h2>
          <p>
            Three numbers decide the length of a time lapse: how long you record, how often
            the camera takes a frame, and how fast those frames play back.
          </p>
          <p>
            <strong>Final video length = recording time ÷ interval ÷ frame rate.</strong>{' '}
            Divide the recording time by the interval to get the frame count, then divide the
            frame count by the playback frame rate to get the clip length.
          </p>
          <p>
            Worked example: 8 hours of recording is 28,800 seconds. At one photo every 5
            seconds that is 5,760 frames. At {PLAYBACK_FPS} fps those frames play for 192
            seconds — <strong>3 minutes 12 seconds</strong>.
          </p>
          <p>
            Run it backwards when you know the clip you want:{' '}
            <strong>recording time = clip length × frame rate × interval</strong>. A
            30-second clip at {PLAYBACK_FPS} fps is 900 frames; at a 5-second interval that is
            4,500 seconds of recording, or 1 hour 15 minutes.
          </p>
        </section>

        <section
          id="howlong_short"
          data-section-id="howlong_short"
          ref={setSectionRef('howlong_short')}
        >
          <h2>Chart: minutes-to-hours shoots</h2>
          <p>
            Rows are how long you record, columns are the capture interval, and each cell is
            the finished clip at {PLAYBACK_FPS} fps.
          </p>
          {renderChart(SHORT_CHART, 'howlong-chart-short')}
          <p>
            The pattern to internalize: halving the interval doubles the clip. If a shoot
            comes out too short, shorten the interval rather than recording longer.
          </p>
        </section>

        <section
          id="howlong_long"
          data-section-id="howlong_long"
          ref={setSectionRef('howlong_long')}
        >
          <h2>Chart: days-to-a-year shoots</h2>
          <p>
            Same layout, longer intervals — the range a construction, renovation, or garden
            shoot lives in. These assume the camera runs around the clock across the whole
            recording time.
          </p>
          {renderChart(LONG_CHART, 'howlong-chart-long')}
          <div className="guide-callout">
            <h3>Real shoots are not round the clock</h3>
            <p>
              A build runs 7am to 5pm on weekdays, not 24/7, and the frames have to fit on a
              card. Give the calculator your schedule and it returns the exact interval,
              frame count, storage size, and final length for your shoot — not a chart row
              near it.
            </p>
            <Link
              to="/tools/timelapse-calculator"
              className="guide-callout-cta"
              data-testid="howlong-calculator-callout"
              onClick={navClick('calculator_from_howlong')}
            >
              Open the free Time Lapse Calculator
            </Link>
          </div>
        </section>

        <section
          id="howlong_reverse"
          data-section-id="howlong_reverse"
          ref={setSectionRef('howlong_reverse')}
        >
          <h2>I want a 30-second clip — how long do I record?</h2>
          <p>
            At {PLAYBACK_FPS} fps a 30-second clip is 900 frames, so the interval decides how
            much real time you have to cover.
          </p>
          <div className="guide-table-wrap">
            <table className="guide-table guide-interval-table" data-testid="howlong-chart-reverse">
              <thead>
                <tr>
                  <th>Interval</th>
                  <th>Record for</th>
                </tr>
              </thead>
              <tbody>
                {REVERSE_CHART.map((row) => (
                  <tr key={row.interval}>
                    <td>{row.interval}</td>
                    <td>{row.record}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>For a 60-second clip, double everything in the right-hand column.</p>
        </section>

        <section
          id="howlong_phone"
          data-section-id="howlong_phone"
          ref={setSectionRef('howlong_phone')}
        >
          <h2>Why your phone ignores all of this</h2>
          <p>
            iPhone and most Android camera apps pick the interval for you and thin out frames
            as the recording grows, so the output usually lands somewhere around 20 to 40
            seconds however long you shoot. Behavior varies by phone and software version, so
            treat that as a rough expectation rather than a rule.
          </p>
          <p>
            The charts above apply to cameras where you set the interval yourself — GoPro,
            Brinno, Afidus, or a DSLR on an intervalometer.
          </p>
        </section>

        <section
          id="howlong_faq"
          data-section-id="howlong_faq"
          ref={setSectionRef('howlong_faq')}
        >
          <h2>FAQ</h2>
          <div className="guide-faq" data-testid="howlong-faq">
            {FAQ.map((item) => (
              <div className="guide-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="guide-final-cta">
          <h2>Planning something longer?</h2>
          <p>
            Length is the easy half. The rest — power, mounting, interval, and turning
            thousands of frames into a video worth watching — is the long-term guide. When
            the frames are on your drive, Tweedia Studio trims the stillness, fixes flicker,
            and exports.
          </p>
          <div className="guide-final-cta-row">
            {studioCta}
            <Link
              to="/guides/long-term-timelapse"
              className="guide-secondary-cta"
              data-testid="howlong-longterm-link"
              onClick={navClick('long_term_guide_from_howlong')}
            >
              Read the long-term guide
            </Link>
            <Link
              to="/tools/timelapse-calculator"
              className="guide-secondary-cta"
              data-testid="howlong-calculator-final"
              onClick={navClick('calculator_from_howlong_final')}
            >
              Open the Calculator
            </Link>
          </div>
          <p className="guide-note">No credit card required to start editing.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default HowLongIsATimelapsePage;
export { FAQ, SHORT_CHART, LONG_CHART, REVERSE_CHART, TOC, SECTION_IDS, PLAYBACK_FPS };
